import { useTranslation } from "react-i18next";import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import {
  Clock,
  User,
  MapPin,
  Package,
  FileText,
  Copy,
  ExternalLink,
  CheckCircle2,
  XCircle } from
'lucide-react';
import { toast } from 'sonner';

const TrailEventModal = ({ event, onClose }) => {const { t } = useTranslation("wms");
  if (!event) return null;

  const formatTimestamp = (ts) => {
    if (!ts) return 'N/A';
    return new Date(ts).toLocaleString();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getStatusBadge = () => {
    const type = event.event_type || '';
    const status = event.scan_result || event.delivery_status;

    if (status === 'OK' || status === 'DELIVERED' || type.includes('COMPLETED')) {
      return <Badge className="bg-green-500">{t("success", "Success")}</Badge>;
    }
    if (status === 'REJECTED' || status === 'FAILED' || type.includes('FAILED')) {
      return <Badge variant="destructive">{t("failed", "Failed")}</Badge>;
    }
    if (status === 'WARNING') {
      return <Badge className="bg-amber-500">{t("warning", "Warning")}</Badge>;
    }
    return <Badge variant="secondary">{t("info", "Info")}</Badge>;
  };

  return (
    <Dialog open={!!event} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />{t("eventDetails", "Event Details")}

            </DialogTitle>
            {getStatusBadge()}
          </div>
        </DialogHeader>

        <Tabs defaultValue="overview" className="mt-4">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="overview">{t("overview", "Overview")}</TabsTrigger>
            <TabsTrigger value="payload">{t("payload", "Payload")}</TabsTrigger>
            <TabsTrigger value="context">{t("context", "Context")}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 mt-4">
            {/* Event Type */}
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">{t("eventType", "Event Type")}</p>
              <p className="font-mono text-sm">{event.event_type || event.type || 'Unknown'}</p>
            </div>

            {/* Key Info Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 border rounded-lg">
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />{t("timestamp", "Timestamp")}

                </p>
                <p className="text-sm font-medium mt-1">
                  {formatTimestamp(event.timestamp_server || event.timestamp)}
                </p>
              </div>
              <div className="p-3 border rounded-lg">
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <User className="h-3 w-3" />{t("actor", "Actor")}

                </p>
                <p className="text-sm font-medium mt-1">
                  {event.actor_id || 'System'}
                  {event.actor_type &&
                  <Badge variant="outline" className="ml-2 text-xs">{event.actor_type}</Badge>
                  }
                </p>
              </div>
              <div className="p-3 border rounded-lg">
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Package className="h-3 w-3" />{t("entity", "Entity")}

                </p>
                <p className="text-sm font-medium mt-1">
                  {event.entity_type || 'N/A'}: {event.entity_id || 'N/A'}
                </p>
              </div>
              <div className="p-3 border rounded-lg">
                <p className="text-xs text-muted-foreground">{t("trailType", "Trail Type")}</p>
                <p className="text-sm font-medium mt-1">
                  <Badge variant="secondary">{event.trail_type || 'GENERAL'}</Badge>
                </p>
              </div>
            </div>

            {/* IDs */}
            <div className="space-y-2">
              <p className="text-sm font-medium">{t("referenceIds", "Reference IDs")}</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {event.id &&
                <div className="flex items-center justify-between p-2 bg-muted/50 rounded">
                    <span className="text-muted-foreground">{t("eventId", "Event ID")}</span>
                    <div className="flex items-center gap-1">
                      <code className="text-xs">{event.id}</code>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(event.id)}>
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                }
                {event.correlation_id &&
                <div className="flex items-center justify-between p-2 bg-muted/50 rounded">
                    <span className="text-muted-foreground">{t("correlation", "Correlation")}</span>
                    <div className="flex items-center gap-1">
                      <code className="text-xs">{event.correlation_id.slice(0, 8)}...</code>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(event.correlation_id)}>
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                }
                {event.order_id &&
                <div className="flex items-center justify-between p-2 bg-muted/50 rounded">
                    <span className="text-muted-foreground">{t("order", "Order")}</span>
                    <code className="text-xs">{event.order_id}</code>
                  </div>
                }
                {event.shipment_id &&
                <div className="flex items-center justify-between p-2 bg-muted/50 rounded">
                    <span className="text-muted-foreground">{t("shipment", "Shipment")}</span>
                    <code className="text-xs">{event.shipment_id}</code>
                  </div>
                }
                {event.route_id &&
                <div className="flex items-center justify-between p-2 bg-muted/50 rounded">
                    <span className="text-muted-foreground">{t("route", "Route")}</span>
                    <code className="text-xs">{event.route_id}</code>
                  </div>
                }
                {event.package_id &&
                <div className="flex items-center justify-between p-2 bg-muted/50 rounded">
                    <span className="text-muted-foreground">{t("package", "Package")}</span>
                    <code className="text-xs">{event.package_id}</code>
                  </div>
                }
              </div>
            </div>
          </TabsContent>

          <TabsContent value="payload" className="mt-4">
            <div className="p-4 bg-slate-900 rounded-lg">
              <pre className="text-sm text-green-400 overflow-x-auto">
                {JSON.stringify(event.payload || event, null, 2)}
              </pre>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => copyToClipboard(JSON.stringify(event.payload || event, null, 2))}>
              
              <Copy className="h-4 w-4 mr-2" />{t("copyPayload", "Copy Payload")}

            </Button>
          </TabsContent>

          <TabsContent value="context" className="space-y-4 mt-4">
            {/* Source */}
            {(event.source_module || event.source_service) &&
            <div className="p-4 border rounded-lg">
                <p className="text-sm font-medium mb-2">{t("source", "Source")}</p>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">{t("module", "Module")}</p>
                    <p>{event.source_module || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("service", "Service")}</p>
                    <p>{event.source_service || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("instance", "Instance")}</p>
                    <p>{event.source_instance || 'N/A'}</p>
                  </div>
                </div>
              </div>
            }

            {/* Geo Location */}
            {(event.geo_lat || event.payload?.geo_stamp) &&
            <div className="p-4 border rounded-lg">
                <p className="text-sm font-medium mb-2 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />{t("location", "Location")}

              </p>
                <p className="text-sm text-muted-foreground">
                  {event.geo_lat || event.payload?.geo_stamp?.lat}, {event.geo_lng || event.payload?.geo_stamp?.lng}
                </p>
              </div>
            }

            {/* Device Info */}
            {event.device_id &&
            <div className="p-4 border rounded-lg">
                <p className="text-sm font-medium mb-2">{t("device", "Device")}</p>
                <p className="text-sm text-muted-foreground">{event.device_id}</p>
              </div>
            }

            {/* Before/After State (for audit events) */}
            {event.payload?.before_state &&
            <div className="p-4 border rounded-lg">
                <p className="text-sm font-medium mb-2">{t("stateChange", "State Change")}</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">{t("before", "Before")}</p>
                    <pre className="text-xs bg-red-50 dark:bg-red-950/20 p-2 rounded">
                      {JSON.stringify(event.payload.before_state, null, 2)}
                    </pre>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">{t("after", "After")}</p>
                    <pre className="text-xs bg-green-50 dark:bg-green-950/20 p-2 rounded">
                      {JSON.stringify(event.payload.after_state, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            }
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>);

};

export default TrailEventModal;